/**
 * Extrai uma mensagem legível de erro da response do DRF.
 *
 * O DRF retorna erros em formatos variados:
 * - { detail: "mensagem" }                                — erros HTTP (404, 401, 500)
 * - { campo: ["msg1", "msg2"] }                           — erros de validação por campo
 * - { campo: "msg" }                                      — variante string única
 * - { non_field_errors: [...] }                           — erros sem campo específico
 * - { campo1: [...], campo2: [...] }                      — múltiplos campos (Slice 4)
 * - "string solta"                                        — fallback raro
 *
 * Estratégia: prefere `detail`, depois `non_field_errors`, depois primeiro
 * campo que tiver mensagem, e por último o fallback genérico.
 */
export function extractApiError(err: unknown, fallback = 'Erro desconhecido.'): string {
  const e = err as {
    response?: { data?: unknown; status?: number }
    message?: string
  }

  const data = e?.response?.data
  if (!data) {
    return e?.message ?? fallback
  }

  if (typeof data === 'string') {
    return data
  }

  if (typeof data !== 'object') {
    return fallback
  }

  const obj = data as Record<string, unknown>

  if (typeof obj.detail === 'string') {
    return obj.detail
  }

  const nonField = obj.non_field_errors
  if (Array.isArray(nonField) && nonField.length > 0) {
    return String(nonField[0])
  }
  if (typeof nonField === 'string') {
    return nonField
  }

  for (const [campo, valor] of Object.entries(obj)) {
    if (campo === 'detail' || campo === 'non_field_errors') continue
    if (Array.isArray(valor) && valor.length > 0) {
      return `${campo}: ${valor[0]}`
    }
    if (typeof valor === 'string') {
      return `${campo}: ${valor}`
    }
  }

  return fallback
}

/**
 * Versão que retorna TODAS as mensagens (útil pra mostrar lista de erros do
 * backend de validação consolidada — ex: ativar PMOC com múltiplos campos
 * faltando depois do PR #49).
 */
export function extractApiErrorList(err: unknown): string[] {
  const e = err as { response?: { data?: unknown } }
  const data = e?.response?.data
  if (!data || typeof data !== 'object') return []

  const obj = data as Record<string, unknown>
  const out: string[] = []

  if (typeof obj.detail === 'string') out.push(obj.detail)

  const nonField = obj.non_field_errors
  if (Array.isArray(nonField)) out.push(...nonField.map(String))
  else if (typeof nonField === 'string') out.push(nonField)

  for (const [campo, valor] of Object.entries(obj)) {
    if (campo === 'detail' || campo === 'non_field_errors') continue
    if (Array.isArray(valor)) {
      valor.forEach((v) => out.push(`${campo}: ${v}`))
    } else if (typeof valor === 'string') {
      out.push(`${campo}: ${valor}`)
    }
  }

  return out
}
